import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METAL_TYPES = [
  "HR Coil",
  "CR Coil",
  "GP Coil",
  "HR Sheet",
  "CR Sheet",
  "GP Sheet",
  "TMT Bar",
  "MS Billet",
  "MS Plate",
  "SS 304 Sheet",
  "SS 316 Sheet",
  "GI Pipe",
  "GI Sheet",
];

const inputClasses = "w-full h-10 rounded-md border border-[#2F4F4F]/40 bg-ib-bg px-3 py-2 text-sm text-ib-text placeholder:text-[#71717A] font-manrope focus:outline-none focus:border-[#007BFF] transition-colors";
const textareaClasses = "w-full min-h-[80px] rounded-md border border-[#2F4F4F]/40 bg-ib-bg px-3 py-2 text-sm text-ib-text placeholder:text-[#71717A] font-manrope focus:outline-none focus:border-[#007BFF] transition-colors resize-none";

export function QuoteForm() {
  const [form, setForm] = useState({
    company_name: "",
    email: "",
    metal_type: "",
    quantity: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/quote`, {
        ...form,
        quantity: parseFloat(form.quantity) || 0,
      });
      setSubmitted(true);
    } catch (_) {
      setError("Failed to submit. Please email us directly at sales@ironbyte.in");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="quote" data-testid="quote-section" className="py-24 px-6">
        <div className="max-w-xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="inline-flex p-4 rounded-full bg-ib-lime/10 mb-6"
          >
            <CheckCircle2 className="w-8 h-8 text-ib-lime" />
          </motion.div>
          <h3 className="font-outfit font-bold text-2xl text-ib-text mb-2">Quote Request Submitted</h3>
          <p className="font-manrope text-sm text-ib-text-secondary mb-4">
            Our team will review your requirements and get back within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-4">
            <a
              href="mailto:sales@ironbyte.in?subject=Quote%20Enquiry"
              data-testid="quote-success-email-sales"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ib-blue/30 font-jetbrains text-xs text-ib-blue hover:bg-ib-blue/10 transition-colors"
            >
              <Mail className="w-3 h-3" />
              sales@ironbyte.in
            </a>
            <a
              href="mailto:ironbytetech@gmail.com?subject=Quote%20Enquiry"
              data-testid="quote-success-email-general"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ib-steel/30 font-jetbrains text-xs text-ib-text-secondary hover:bg-ib-steel/10 transition-colors"
            >
              <Mail className="w-3 h-3" />
              ironbytetech@gmail.com
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="quote" data-testid="quote-section" className="py-24 px-6 relative">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase">
            Get Started
          </span>
          <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl tracking-tight mt-3 text-ib-text">
            Request a <span className="text-ib-blue">Custom Quote</span>
          </h2>
          <p className="font-manrope text-base text-ib-text-secondary mt-3">
            Tell us what you need. We'll match you with the best suppliers and pricing.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-4">
            <a href="mailto:sales@ironbyte.in?subject=Product%20Enquiry" data-testid="quote-email-sales" className="inline-flex items-center gap-1.5 font-jetbrains text-xs text-ib-blue hover:text-ib-lime transition-colors">
              <Mail className="w-3 h-3" /> sales@ironbyte.in
            </a>
            <span className="w-1 h-1 rounded-full bg-ib-steel" />
            <a href="mailto:ironbytetech@gmail.com?subject=Product%20Enquiry" data-testid="quote-email-general" className="inline-flex items-center gap-1.5 font-jetbrains text-xs text-ib-text-secondary hover:text-ib-blue transition-colors">
              <Mail className="w-3 h-3" /> ironbytetech@gmail.com
            </a>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          onSubmit={handleSubmit}
          className="rounded-xl border border-[#2F4F4F]/30 bg-ib-surface p-8 space-y-5 relative z-[41]"
          data-testid="quote-form"
        >
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 font-manrope text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Company Name</label>
              <input
                data-testid="quote-company-input"
                placeholder="Your Company"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                required
                className={inputClasses}
              />
            </div>
            <div>
              <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Email</label>
              <input
                data-testid="quote-email-input"
                type="email"
                placeholder="you@company.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className={inputClasses}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Metal Type</label>
              <Select
                value={form.metal_type}
                onValueChange={(val) => setForm({ ...form, metal_type: val })}
              >
                <SelectTrigger
                  data-testid="quote-metal-select"
                  className="bg-ib-bg border-[#2F4F4F]/40 text-ib-text font-manrope text-sm h-10"
                >
                  <SelectValue placeholder="Select metal type" />
                </SelectTrigger>
                <SelectContent className="bg-ib-surface border-[#2F4F4F]/40">
                  {METAL_TYPES.map((type) => (
                    <SelectItem key={type} value={type} className="text-ib-text font-manrope text-sm hover:bg-[#2F4F4F]/20">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Quantity (tons)</label>
              <input
                data-testid="quote-quantity-input"
                type="number"
                placeholder="e.g. 500"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                required
                className={inputClasses}
              />
            </div>
          </div>

          <div>
            <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Additional Details</label>
            <textarea
              data-testid="quote-message-input"
              placeholder="Specifications, grade requirements, delivery timeline..."
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={4}
              className={textareaClasses}
            />
          </div>

          <Button
            type="submit"
            data-testid="quote-submit-btn"
            disabled={loading}
            className="w-full bg-ib-blue hover:bg-ib-blue/90 text-white font-outfit gap-2 py-6 rounded-full transition-all duration-200 hover:shadow-[0_0_30px_rgba(0,123,255,0.3)]"
          >
            {loading ? "Submitting..." : "Submit Quote Request"}
            <Send className="w-4 h-4" />
          </Button>
        </motion.form>
      </div>
    </section>
  );
}
